import type { StoreApi } from 'zustand'
import type { AppState, ChallengeModule, ChallengeModuleStats, ChallengeSettings, ChallengeBattleRecord, ChallengeState } from '@/store/rootTypes'

type SetState = StoreApi<AppState>['setState']
type GetState = StoreApi<AppState>['getState']

const MAX_RECENT_RECORDS = 20

export function createEmptyModuleStats(): ChallengeModuleStats {
  return {
    totalBattles: 0,
    wins: 0,
    losses: 0,
    totalStars: 0,
    bestCombo: 0,
    maxWinStreak: 0,
    currentStreak: 0,
    bestStars: {},
    completedStages: [],
    recentRecords: [],
  }
}

export function createDefaultChallengeSettings(): ChallengeSettings {
  return {
    difficulty: 'normal',
    questionCount: 8,
    timeLimit: 60,
    maxHp: 3,
  }
}

export function createInitialChallengeState(): ChallengeState {
  return {
    moduleStats: {
      listen: createEmptyModuleStats(),
      speak: createEmptyModuleStats(),
      read: createEmptyModuleStats(),
      write: createEmptyModuleStats(),
    },
    moduleSettings: {
      listen: createDefaultChallengeSettings(),
      speak: createDefaultChallengeSettings(),
      read: createDefaultChallengeSettings(),
      write: createDefaultChallengeSettings(),
    },
  }
}

export function createChallengeSlice(set: SetState, get: GetState): Pick<AppState,
  'challengeStats' | 'recordChallengeResult' | 'updateChallengeSettings' | 'resetChallengeStats'
> {
  return {
    challengeStats: createInitialChallengeState(),

    recordChallengeResult: (module: ChallengeModule, result: Omit<ChallengeBattleRecord, 'timestamp'>) => {
      const state = get()
      const prev = state.challengeStats
      const prevModuleStats = prev.moduleStats ?? createInitialChallengeState().moduleStats
      const prevModule = prevModuleStats[module] ?? createEmptyModuleStats()

      const record: ChallengeBattleRecord = { ...result, timestamp: Date.now() }

      const newBestStars = { ...prevModule.bestStars }
      newBestStars[result.stageId] = Math.max(newBestStars[result.stageId] ?? 0, result.stars)

      const completedStages = result.won && !prevModule.completedStages.includes(result.stageId)
        ? [...prevModule.completedStages, result.stageId]
        : prevModule.completedStages

      const newCurrentStreak = result.won ? prevModule.currentStreak + 1 : 0
      const newMaxWinStreak = Math.max(prevModule.maxWinStreak, newCurrentStreak)

      const recentRecords = [record, ...prevModule.recentRecords].slice(0, MAX_RECENT_RECORDS)

      const newModuleStats: ChallengeModuleStats = {
        totalBattles: prevModule.totalBattles + 1,
        wins: prevModule.wins + (result.won ? 1 : 0),
        losses: prevModule.losses + (result.won ? 0 : 1),
        totalStars: prevModule.totalStars + result.stars,
        bestCombo: Math.max(prevModule.bestCombo, result.maxCombo),
        maxWinStreak: newMaxWinStreak,
        currentStreak: newCurrentStreak,
        bestStars: newBestStars,
        completedStages,
        recentRecords,
      }

      const newChallengeStats: ChallengeState = {
        ...prev,
        moduleStats: { ...prevModuleStats, [module]: newModuleStats },
      }

      set({ challengeStats: newChallengeStats })
    },

    updateChallengeSettings: (module: ChallengeModule, settings: Partial<ChallengeSettings>) => {
      const state = get()
      const prev = state.challengeStats
      // 防御性处理：如果持久化数据缺少 moduleSettings（旧版本遗留），
      // 用初始值补全，避免 prev.moduleSettings[module] 抛 TypeError
      const prevModuleSettings = prev.moduleSettings ?? createInitialChallengeState().moduleSettings
      const prevModuleSetting = prevModuleSettings[module] ?? createDefaultChallengeSettings()
      set({
        challengeStats: {
          ...prev,
          moduleSettings: {
            ...prevModuleSettings,
            [module]: { ...prevModuleSetting, ...settings },
          },
        },
      })
    },

    resetChallengeStats: (module?: ChallengeModule) => {
      const state = get()
      const prev = state.challengeStats
      if (module) {
        const prevModuleStats = prev.moduleStats ?? createInitialChallengeState().moduleStats
        set({
          challengeStats: {
            ...prev,
            moduleStats: {
              ...prevModuleStats,
              [module]: createEmptyModuleStats(),
            },
          },
        })
      } else {
        set({ challengeStats: createInitialChallengeState() })
      }
    },
  }
}
